import React, { useCallback, useEffect, useRef, useState } from 'react';
import './Martini.css';

export default function Martini({ className = '', onActivate = null, quoteText = null }) {
  const [wiggle, setWiggle] = useState(false);
  const wiggleTimerRef = useRef(null);

  const triggerWiggle = useCallback(() => {
    setWiggle(true);
    if (wiggleTimerRef.current) {
      clearTimeout(wiggleTimerRef.current);
    }
    wiggleTimerRef.current = setTimeout(() => {
      setWiggle(false);
      wiggleTimerRef.current = null;
    }, 600);
  }, []);

  useEffect(
    () => () => {
      if (wiggleTimerRef.current) clearTimeout(wiggleTimerRef.current);
    },
    []
  );

  const handleInteract = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      triggerWiggle();
      if (typeof onActivate === 'function') onActivate();
    },
    [onActivate, triggerWiggle]
  );

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleInteract(e);
      }
    },
    [handleInteract]
  );

  return (
    <div
      className={`martini ${className}`}
      role="button"
      tabIndex={0}
      aria-label="Martini—tap for a random Bond quote"
      onClick={handleInteract}
      onKeyDown={onKeyDown}
    >
      {quoteText && (
        <div className="martini__bubble" aria-live="polite">
          <p className="martini__bubble-text">{quoteText}</p>
        </div>
      )}
      <svg viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        {/* Glass bowl — triangular martini shape */}
        <polygon points="50,95 15,30 85,30" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.45)" strokeWidth="2" strokeLinejoin="round" />
        {/* Liquid */}
        <polygon points="50,78 23,38 77,38" fill="rgba(200,220,140,0.18)" />
        {/* Liquid surface line */}
        <line x1="23" y1="38" x2="77" y2="38" stroke="rgba(200,220,140,0.3)" strokeWidth="1" />
        {/* Stem */}
        <line x1="50" y1="95" x2="50" y2="110" stroke="rgba(255,255,255,0.5)" strokeWidth="2.5" strokeLinecap="round" />
        {/* Base */}
        <ellipse cx="50" cy="112" rx="16" ry="4" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" />
        {/* Rim highlight */}
        <line x1="15" y1="30" x2="85" y2="30" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinecap="round" />
        {/* Toothpick + olive group */}
        <g className={`martini__olive-group ${wiggle ? 'martini__olive-group--wiggle' : ''}`}>
          {/* Toothpick */}
          <line x1="32" y1="28" x2="68" y2="52" stroke="#c4a35a" strokeWidth="1.8" strokeLinecap="round" />
          {/* Olive */}
          <ellipse cx="50" cy="40" rx="7" ry="6" fill="#4a6b2e" stroke="#3a5522" strokeWidth="1.2" />
          {/* Olive pimento */}
          <ellipse cx="50" cy="40" rx="2.5" ry="2" fill="#c0392b" />
          {/* Olive highlight */}
          <ellipse cx="48" cy="38" rx="2" ry="1.2" fill="rgba(255,255,255,0.2)" />
        </g>
        {/* Glass reflections */}
        <line x1="22" y1="35" x2="35" y2="65" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="78" y1="35" x2="65" y2="65" stroke="rgba(255,255,255,0.07)" strokeWidth="1" strokeLinecap="round" />
      </svg>
    </div>
  );
}
